/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useDeferredValue, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getAllAccountIds} from '@ion/core/src/services/FetchWorkerUtils';
import {isFalse} from '@ion/core/src/utils/BooleanUtils';
import {getDateFromToday} from '@ion/core/src/utils/DateUtil';
import {
  getHistoryColors,
  getLastPosition,
  getLatestRecordByField,
  mergeArrayOrObject
} from '@ion/dls/src/maps/helpers/MapViewUtils';
import LiveHistoryEventLineLayer from '@ion/dls/src/maps/layers/LiveHistoryEventLineLayer';
import LiveHistoryLayer from '@ion/dls/src/maps/layers/LiveHistoryLayer';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import {dlsColor} from '@ion/dls/src/theme/theme';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {useLoadHistoryAssetsV2} from '@ion/query/src/hooks/useLoadHistoryAssetsV2';
import {useLoadHistoryAssetUpdatesV2} from '@ion/query/src/hooks/useLoadHistoryAssetUpdatesV2';
import {
  setHistoryInputEvents,
  setLiveHistoryFetching,
  setLiveHistorySelectedAsset
} from '@/store/actions/tracking';
import {globalGroupIdSelector, globalGroupStateSelector} from '@/store/selectors/global-group-chooser-selectors';
import {
  assetLabelContentSelector,
  liveHistoryActiveSelector,
  liveHistoryAssetSearchListSelector,
  liveHistoryDisplayReportSelector,
  liveHistoryInitialAssetsSelector,
  liveHistoryInputEventsSelector,
  liveHistoryIsFetchingSelector,
  liveHistoryIsUIFetchingSelector,
  liveHistoryReportTypeSelector,
  liveHistoryRingsSelector,
  liveHistorySearchTimeSelector,
  liveHistorySelectedAssetSelector,
  mapBasemapSelector,
  mapLayerSelector
} from '@/store/selectors/tracking-selectors';
import {getLastAssetPage, getLastAVLEvent} from '../../../helpers';
import {useTrackedFleetUpdate} from '../../../hooks/useTrackedFleetUpdate';
import useMapToolProps from '../hooks/useMapToolProps';

const DELAY_TIME    = 1500;
const NO_DELAY_TIME = 0;

// Getting a list of Colors
const colorList = Object.values(getHistoryColors());

// Persistent mapping of assetId → color
let assetColorMap: Record<string, string> = {};
  
// Persistent index to track the next unused color in the palette
let nextColorIndex = 0;

const getColorForPolyline = assetId => {
  if (!assetColorMap[assetId]) {
    assetColorMap[assetId] = colorList[nextColorIndex % colorList.length] || dlsColor('grey1');
    nextColorIndex++;
  }
  return assetColorMap[assetId];
};

const constructAssets = assets => {
  if (assets?.length) {
    assets.forEach(asset => {
      asset.color = getColorForPolyline(asset.assetId);
    });
  }
  return assets;
};

const LiveHistoryLayerContainer = ({map, view}: MapContainerProps) => {
  const dispatch                  = useDispatch();
  const isMounted                 = useHasMounted(null as any);
  const mapToolProps              = useMapToolProps();
  const globalGroupState          = useSelector(globalGroupStateSelector);
  const globalGroupId             = useSelector(globalGroupIdSelector);
  const layers                    = useSelector(mapLayerSelector);
  const basemap                   = useSelector(mapBasemapSelector);
  const labelContent              = useSelector(assetLabelContentSelector);
  const isActive                  = useSelector(liveHistoryActiveSelector);
  const reportType                = useSelector(liveHistoryReportTypeSelector);
  const inputEvents               = useSelector(liveHistoryInputEventsSelector);
  const displayReport             = useSelector(liveHistoryDisplayReportSelector);
  const reportTime                = useSelector(liveHistorySearchTimeSelector);
  const isHistoryFetching         = useSelector(liveHistoryIsFetchingSelector);
  const isUIFetching              = useSelector(liveHistoryIsUIFetchingSelector);
  const assetsSearchList          = useSelector(liveHistoryAssetSearchListSelector);
  const initialAssets             = useSelector(liveHistoryInitialAssetsSelector);
  const selectedAsset             = useSelector(liveHistorySelectedAssetSelector);
  const reportTimeRef             = useRef(reportTime);
  const lastAssetPositionRef      = useRef([]);
  const assetsRef                 = useRef([]);
  const assetRoutesRef            = useRef({});
  const {onLiveHistoryUIFetching} = mapToolProps || {};
  const rings                     = useSelector(liveHistoryRingsSelector);

  useEffect(() => {
    if (isHistoryFetching) {
      lastAssetPositionRef.current = [];
      assetsRef.current            = [];
      assetRoutesRef.current       = {};
      assetColorMap                = {};
      nextColorIndex               = 0;
    }
  }, [isHistoryFetching]);

  useEffect(() => {
    reportTimeRef.current = reportTime;
  }, [reportTime]);

  const queryParams = useMemo(() => {
    if (isActive && (assetsSearchList?.length || rings?.length)) {
      const params = {} as any;
      if (assetsSearchList?.length) {
        params.assets = assetsSearchList;
      }
      if (rings?.length) {
        params.geometry = rings;
        params.groups   = params.assets?.length ? [] : getAllAccountIds(globalGroupState, entityId(globalGroupId));
      }
      const dates  = getDateFromToday(reportTimeRef.current);
      params.start = dates?.fromDate;
      params.end   = dates?.toDate;
      return {params};
    }
    return null;
  }, [
    isActive,
    displayReport,
    assetsSearchList,
    rings,
    globalGroupId
  ]);

  const {
    results: historyResults,
    isFetching,
    hasNextPage,
    isSuccess
  } = useLoadHistoryAssetsV2({
    queryParams,
    baseKey : QueryKey.LOAD_HVS_ASSETS,
    enabled : isActive && Boolean(queryParams)
  });

  const historyAssetResults = useMemo(() => getLastAssetPage(historyResults), [historyResults]);

  const {assets: assetsData, assetRoutes} = historyAssetResults || {} as any;

  const assets = useMemo(() => constructAssets(assetsData), [assetsData]);

  const queryUpdatesParams = useMemo(() => {
    if (isActive && (assetsSearchList?.length || rings?.length)) {
      const params = {} as any;
      if (assetsSearchList?.length) {
        params.assets = assetsSearchList;
      }
      if (rings?.length) {
        params.geometry = rings;
        params.groups   = params.assets?.length ? [] : getAllAccountIds(globalGroupState, entityId(globalGroupId));
      }
      return {params};
    }
    return null;
  }, [
    isActive,
    displayReport,
    assetsSearchList,
    rings,
    globalGroupId
  ]);
  
  const {
    results    : historyUpdatesResults,
    isFetching : isFetchingUpdates,
    refetch,
    isLoading,
    isFetchingNextPage,
    status
  } = useLoadHistoryAssetUpdatesV2({
    parentQueryParams : queryParams,
    queryParams       : queryUpdatesParams,
    assets            : assetsRef.current,
    parentBaseKey     : QueryKey.LOAD_HVS_ASSETS,
    baseKey           : QueryKey.LOAD_HVS_ASSETS_UPDATES,
    enabled           : isActive && Boolean(queryUpdatesParams) && isFalse(hasNextPage)
  });

  const historyAssetUpdates = useMemo(() => getLastAssetPage(historyUpdatesResults), [historyUpdatesResults]);

  const {
    assets: assetUpdatesData,
    assetRoutes: assetRouteUpdates
  } = historyAssetUpdates || {} as any;

  const assetUpdatesResults = useMemo(() => constructAssets(assetUpdatesData), [assetUpdatesData]);
  
  useTrackedFleetUpdate(
    'liveHistory',
    refetch,
    !(isActive && isFalse(hasNextPage)) ||
    (isLoading || isUIFetching || isFetchingUpdates || isFetchingNextPage || status === 'idle')
  );

  useEffect(() => {
    const fetching = isFetching && !isSuccess;
    const time     = fetching ? NO_DELAY_TIME : DELAY_TIME;
    const delay    = setTimeout(() => {
      dispatch(setLiveHistoryFetching(fetching));
      clearInterval(delay);
    }, time);
  }, [isFetching, isSuccess]);

  useEffect(() => {
    if (assets?.length) {
      assetsRef.current = getLastAVLEvent({data: assets, dataSaved: assetsRef.current});
      dispatch(setHistoryInputEvents(assets as any, true) as any);
    }
  }, [assets]);

  useEffect(() => {
    if (assetUpdatesResults?.length) {
      assetsRef.current = getLastAVLEvent({data: assetUpdatesResults, dataSaved: assetsRef.current});
    }
  }, [assetUpdatesResults]);

  const updateSelectedAsset = useCallback(assetUpdates => {
    if (selectedAsset && assetUpdates?.length) {
      const {assetId : selectedAssetId} = selectedAsset || {};
      const assetsFiltered = assetUpdates.filter(({assetId}) =>
        assetId === selectedAssetId);
      if (assetsFiltered?.length) {
        const lastAVLEvent = getLatestRecordByField(assetsFiltered, selectedAssetId, 'eventTime');
        lastAVLEvent && dispatch(setLiveHistorySelectedAsset({...lastAVLEvent, isCvsAsset: true}));
      }
    }
  }, [selectedAsset]);

  const lastAssetsPosition = useMemo(() => {
    if (!assets?.length && !assetUpdatesResults?.length) {
      lastAssetPositionRef.current = [];
      return mergeArrayOrObject(lastAssetPositionRef.current, initialAssets);
    }
    if (assetUpdatesResults?.length) {
      lastAssetPositionRef.current = getLastPosition(
        [...assetUpdatesResults, ...lastAssetPositionRef.current, ...initialAssets],
        reportType,
        inputEvents
      );
    } else if (assets?.length && !lastAssetPositionRef.current?.length) {
      lastAssetPositionRef.current = getLastPosition(
        [...assets, ...lastAssetPositionRef.current, ...initialAssets],
        reportType,
        inputEvents
      );
    }
    updateSelectedAsset(lastAssetPositionRef.current);
    return lastAssetPositionRef.current;
  }, [assets, assetUpdatesResults, reportType, inputEvents, initialAssets]);

  const routesData       = useMemo(() => assetRoutes || [], [assetRoutes]);
  const routeUpdatesData = useMemo(() => assetRouteUpdates || [], [assetRouteUpdates]);

  // defers the heavy map layer re-render these trigger once react-query's cache updates for
  // the 'liveHistory' auto-refresh cycle, since startTransition alone doesn't cover an async
  // cache update landing after the call that started it ...
  const deferredAssetUpdatesResults = useDeferredValue(assetUpdatesResults);
  const deferredRouteUpdatesData    = useDeferredValue(routeUpdatesData);
  const deferredLastAssetsPosition  = useDeferredValue(lastAssetsPosition);

  const layerProps = useMemo(() => ({
    assetsUpdates            : deferredAssetUpdatesResults,
    routes                   : routesData,
    routesUpdates            : deferredRouteUpdatesData,
    lastPositions            : deferredLastAssetsPosition,
    setLiveHistoryUIFetching : onLiveHistoryUIFetching,
    assets,
    layers,
    basemap,
    isActive,
    reportTime,
    reportType,
    inputEvents,
    labelContent,
    isHistoryFetching
  }), [
    assets,
    layers,
    basemap,
    isActive,
    reportTime,
    reportType,
    routesData,
    inputEvents,
    labelContent,
    isHistoryFetching,
    deferredRouteUpdatesData,
    deferredLastAssetsPosition,
    deferredAssetUpdatesResults,
    onLiveHistoryUIFetching
  ]);
  
  if (isMounted) {
    return (
      <>
        <LiveHistoryLayer map={map}
          view={view}
          layerProps={layerProps as any} />
        <LiveHistoryEventLineLayer map={map}
          view={view}
          layerProps={layerProps as any} />
      </>
    );
  }
};

export default LiveHistoryLayerContainer;
